import { apiClient } from '../api/axiosInstance';
import { cachedFetch } from '../api/apiCache';
import type { Location, LocationObject } from '../types';

/**
 * Locations reference-data service.
 *
 * Supplies the location list used to populate the location picker on the
 * submit-claim and edit-claim forms, and converts a selected location into the
 * encounter-service `LocationObject` wire shape. Shared by both features so the
 * mapping lives in one place.
 *
 * The data is currently sourced from the QB Adapter (`/api/v1/qb/locations`),
 * but consumers only see entity-shaped `Location` objects — the source is an
 * implementation detail of this module and may change (e.g. to a local table)
 * without affecting callers.
 *
 * Responses are cached (`cachedFetch`) so repeated mounts across pages reuse a
 * single in-flight / recently-fetched result.
 */

/**
 * Wire shape of a single location returned by the upstream adapter. The adapter
 * is faithful to its source — there is no canonical pre-concatenated "full
 * address" column, so the wire format exposes raw parts and the UI builds the
 * display string. See specs/06-submit-claim-ui/data-model.md and
 * specs/07-qb-provider-location-api.
 */
export interface LocationDtoWire {
  location_id: string;
  location_name: string;
  address_line_1?: string | null;
  address_line_2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  npi?: string | null;
  related_location_id?: string | null;
}

/**
 * Build the human-readable display address from raw location parts in
 * US-postal style (`street, city, state zip`), skipping any empty/null/
 * whitespace-only fields. Returns `undefined` when no address parts are
 * present (the dropdown then falls back to `name` only).
 *
 * Exported for unit testing.
 */
export function buildAddress(l: Partial<LocationDtoWire>): string | undefined {
  const trim = (s: string | null | undefined): string =>
    typeof s === 'string' ? s.trim() : '';
  const present = (s: string): boolean => s.length > 0;

  const street = [trim(l.address_line_1), trim(l.address_line_2)]
    .filter(present)
    .join(', ');

  const stateZip = [trim(l.state), trim(l.zip)].filter(present).join(' ');
  const cityStateZip = [trim(l.city), stateZip].filter(present).join(', ');

  const result = [street, cityStateZip].filter(present).join(', ');
  return result.length > 0 ? result : undefined;
}

/** Normalize an optional string-or-null part: trimmed value when present, else undefined. */
function presentString(raw: string | null | undefined): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/** Map a single upstream wire location to the entity-shaped {@link Location}. */
function toLocation(l: LocationDtoWire): Location {
  const addressLine1 = presentString(l.address_line_1);
  const addressLine2 = presentString(l.address_line_2);
  const city = presentString(l.city);
  const state = presentString(l.state);
  const zip = presentString(l.zip);
  const npi = presentString(l.npi);
  const relatedLocationId = presentString(l.related_location_id);

  return {
    id: l.location_id,
    name: l.location_name,
    address: buildAddress(l),
    ...(addressLine1 ? { addressLine1 } : {}),
    ...(addressLine2 ? { addressLine2 } : {}),
    ...(city ? { city } : {}),
    ...(state ? { state } : {}),
    ...(zip ? { zip } : {}),
    ...(npi ? { npi } : {}),
    ...(relatedLocationId ? { relatedLocationId } : {}),
  };
}

export function getLocations(): Promise<Location[]> {
  return cachedFetch('locations', async () => {
    const response = await apiClient.get<LocationDtoWire[]>('/api/v1/qb/locations');
    return response.data.map(toLocation);
  });
}

/**
 * Cross-tenant location list for the back office. Unlike {@link getLocations} (which the
 * gateway scopes to the caller's tenant via the JWT), the back office is cross-tenant and
 * passes the encounter's tenant id explicitly to the qb-adapter's by-tenant endpoint.
 * Cached per tenant.
 */
export function getLocationsByTenant(tenantId: string): Promise<Location[]> {
  return cachedFetch(`locations:${tenantId}`, async () => {
    const response = await apiClient.get<LocationDtoWire[]>('/api/v1/qb/locations', {
      params: { tenantId },
    });
    return response.data.map(toLocation);
  });
}

/**
 * Imagine ("service facility") locations for the back office. Same owner-scoped source as
 * {@link getLocationsByTenant} but from the qb-adapter's imagine endpoint, which is NOT
 * de-duplicated — every facId + locName row is a distinct option (the caller renders them as-is
 * without collapsing). `id` is the QuickBase locServiceFacID; `name` is locName. Cached per tenant.
 */
export function getImagineLocationsByTenant(tenantId: string): Promise<Location[]> {
  return cachedFetch(`imagine-locations:${tenantId}`, async () => {
    const response = await apiClient.get<LocationDtoWire[]>('/api/v1/qb/imagine-locations', {
      params: { tenantId },
    });
    return response.data.map(toLocation);
  });
}

/**
 * Build the encounter-service `LocationObject` wire shape from the FE-side
 * locations cache and the form's selected location id. Per data-model.md
 * §2.5 — empty / missing address parts are omitted (encounter-service treats
 * them as optional).
 */
export function locationToWire(
  locations: Location[],
  selectedId: string,
): LocationObject {
  const loc = locations.find((l) => l.id === selectedId);
  if (!loc) throw new Error('Selected location not found in locations cache');
  return {
    locationId: loc.id,
    locationName: loc.name,
    ...(loc.addressLine1 ? { addressLine1: loc.addressLine1 } : {}),
    ...(loc.addressLine2 ? { addressLine2: loc.addressLine2 } : {}),
    ...(loc.city ? { city: loc.city } : {}),
    ...(loc.state ? { state: loc.state } : {}),
    ...(loc.zip ? { zip: loc.zip } : {}),
    ...(loc.npi ? { npi: loc.npi } : {}),
  };
}
